interface ReviewSchemaData {
  reviewId: string
  author?: {
    name?: string
    role?: string
    jobTitle?: string
  }
  reviewRating: number
  reviewTitle?: string
  reviewBody: string
  positiveNotes?: string
  negativeNotes?: string
  datePublished: string
  company: {
    name: string
    permalink: string
    description?: string
    logo?: string
    website?: string
  }
}

export function generateReviewSchema(data: ReviewSchemaData) {
  if (!data.company.name || !data.reviewBody || !data.reviewRating) {
    return null
  }

  // Build author object
  const authorJobTitle = data.author?.jobTitle || data.author?.role || 'Employee'
  const authorName = data.author?.name || `${authorJobTitle} at ${data.company.name}`

  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    'author': {
      '@type': 'Person',
      'name': authorName,
      'jobTitle': authorJobTitle,
      'worksFor': {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': data.company.name,
        ...(data.company.logo && { logo: data.company.logo }),
        ...(data.company.website && { sameAs: data.company.website }),
        ...(data.company.description && { description: data.company.description })
      }
    },
    'reviewRating': {
      '@type': 'Rating',
      'ratingValue': data.reviewRating,
      'bestRating': 5,
      'worstRating': 1
    },
    'reviewBody': data.reviewBody,
    'itemReviewed': {
      '@type': 'Organization',
      'name': data.company.name,
      ...(data.company.logo && { logo: data.company.logo })
    },
    'datePublished': data.datePublished
  }

  // Add optional headline if title exists
  if (data.reviewTitle) {
    schema.name = data.reviewTitle
  }

  // Add positive notes (pros) if available
  if (data.positiveNotes && data.positiveNotes.trim()) {
    schema.positiveNotes = {
      '@type': 'ItemList',
      'itemListElement': data.positiveNotes
    }
  }

  // Add negative notes (cons) if available
  if (data.negativeNotes && data.negativeNotes.trim()) {
    schema.negativeNotes = {
      '@type': 'ItemList',
      'itemListElement': data.negativeNotes
    }
  }

  return schema
}

export function generateReviewsListSchema(reviews: any[], company: { name: string, permalink: string, logo?: string, description?: string, website?: string }) {
  if (!reviews || reviews.length === 0) {
    return []
  }

  const schemas = reviews
    .filter((review) => {
      // Only include approved reviews with required fields
      return (
        review.status === 'approved'
        && review.text
        && review.overall_rating
        && review.date
      )
    })
    .map((review) => {
      const reviewSchema = generateReviewSchema({
        reviewId: review._id,
        author: {
          name: review.author?.name,
          role: review.author?.role,
          jobTitle: review.job_title
        },
        reviewRating: review.overall_rating,
        reviewTitle: review.title,
        reviewBody: review.text,
        positiveNotes: review.pros,
        negativeNotes: review.cons,
        datePublished: new Date(review.date).toISOString().split('T')[0],
        company: {
          name: company.name,
          permalink: company.permalink,
          logo: company.logo,
          description: company.description,
          website: company.website
        }
      })

      return reviewSchema
    })
    .filter(schema => schema !== null)

  return schemas
}
